<template>
  <div class="flex flex-1 gap-x-2 md:gap-x-6">
    <!-- {{ data }} -->
    <!-- {{ productData }} -->
    <div
      :class="
        twJoin(
          'w-16 md:w-[130px] h-16 md:h-[130px] bg-primary-white rounded-lg overflow-hidden',
          isOutOfStock && 'opacity-60'
        )
      "
    >
      <SkeletonLoader v-if="isLoading" class="w-full h-full" />
      <img
        v-if="
          data.personalization_base64 || data?.personalization?.thumbnail_base64
        "
        alt=""
        :src="
          data.personalization_base64 || data?.personalization?.thumbnail_base64
        "
        :class="twJoin('w-full h-full object-contain md:py-4 py-2')"
      />
      <img
        v-else-if="productData?.thumbnailUrl && !data.metadata"
        alt=""
        class="object-contain w-full h-full"
        :src="config.public.mediaUrl + '/' + productData?.thumbnailUrl"
      />
      <PixiView
        v-else-if="data.metadata || data.personalization"
        :metadata="{
          artworkBackgroundUrl: data?.metadata?.backgroundUrl,
          text: data.metadata?.personalizeText.raw.split(''),
          textPosition: data.metadata?.textPositionName,
          textStyle: data.metadata?.textStyleName,
          textColors: data.metadata?.textColorsValue,
          personalization: data?.personalization,
          deviceList: props?.deviceList
        }"
        class="object-contain w-full h-full pt-3 pb-2"
        @change="(base64: string) => handleExtractBase64(base64)"
      />
    </div>
    <SkeletonLoader v-if="isLoading" class="flex flex-1" />
    <div v-else class="flex flex-1">
      <div :class="twJoin('flex-1', isOutOfStock && 'opacity-60')">
        <NuxtLinkLocale
          :to="
            data?.personalization
              ? '/personalization-cases'
              : itemSlug?.artworkSlug
                ? `/product/${itemSlug?.deviceSlug}/${itemSlug?.productSlug}/${itemSlug?.artworkSlug}?variant=${encodeURIComponent(itemSlug?.variantName || '')}`
                : `/product/${itemSlug?.deviceSlug}/${itemSlug?.productSlug}?variant=${encodeURIComponent(itemSlug?.variantName || '')}`
          "
          @click="closeCart"
        >
          <div class="font-bold mb-2 md:mb-[10px]">{{ productData?.name }}</div>
        </NuxtLinkLocale>
        <div
          v-if="productData?.deviceName !== productData?.name"
          class="text-sm md:tex-sm"
        >
          <span class="font-bold">{{ $t('cart.device') }}:</span>
          {{ productData?.deviceName }}
        </div>
        <div
          v-if="productData?.productName !== productData?.deviceName"
          class="text-sm md:tex-sm"
        >
          <span class="font-bold">{{ $t('cart.type') }}:</span>
          {{ productData?.productName }}
        </div>
        <div
          v-if="productData?.variantName !== 'Default'"
          class="text-sm md:text-sm"
        >
          <span class="font-bold">{{ $t('cart.colors') }}:</span>
          {{ productData?.variantName }}
        </div>
        <div class="mt-2 font-bold md:hidden">
          {{
            displayCommaNumber((productData?.price || 0) * data?.quantity, 2)
          }}
          THB
        </div>
      </div>
      <div class="flex flex-col items-end justify-between">
        <div
          class="h-10 bg-primary-white flex flex-row justify-center items-center rounded-full px-[10px] font-bold select-none"
        >
          <div
            class="flex items-center justify-center w-8 h-8 cursor-pointer"
            @click="
              () => {
                data?.quantity === 1 && emit('delete')
                handleDecreaseQuantity(data?.cartItemId, data.quantity)
              }
            "
          >
            <Icon v-if="data?.quantity == 1" name="trash" />
            <Icon v-if="data?.quantity > 1" name="minus" />
          </div>
          <div class="flex items-center justify-center w-8 h-8">
            {{ data?.quantity }}
          </div>
          <div
            :class="
              twJoin(
                'w-8 h-8 flex items-center justify-center cursor-pointer',
                productStocks && data?.quantity + 1 <= productStocks?.stocks
                  ? 'opacity-100'
                  : 'opacity-50 pointer-events-none'
              )
            "
            @click="() => handleIncreaseQuantity(data.cartItemId)"
          >
            <Icon name="plus" />
          </div>
        </div>
        <div class="hidden font-bold md:block">
          {{
            displayCommaNumber((productData?.price || 0) * data?.quantity, 2)
          }}
          THB
        </div>
        <div
          v-if="isOutOfStock"
          class="px-4 py-1 font-bold text-white bg-black rounded-2xl"
        >
          {{ $t('cart.outOfStock') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { twJoin } from 'tailwind-merge'
import { find, findIndex } from 'lodash-es'
import { productRepository } from '~/repository/productRepository'

const config = useRuntimeConfig()

const props = defineProps<{
  data: CartState['items'][number]
  deviceList: GetDeviceAllResponse['data']
}>()
const emit = defineEmits(['loaded', 'delete'])
const isOutOfStock = ref<boolean | undefined>()

const { closeCart } = useWebsiteStore()
const cartStore = useCartStore()
const { itemsData, items } = storeToRefs(cartStore)
const {
  handleDecreaseQuantity: handleCartStoreDecreaseQuantity,
  handleIncreaseQuantity,
  handleSetQuantity,
  removeFromCart,
  updateProductData,
  handleUpdateBase64
} = useCartStore()
const { on } = useEventTracking()

const isLoading = ref(true)
const productData = computed(() =>
  find(itemsData.value, (item) => item.cartItemId === props.data.cartItemId)
)

const handleDecreaseQuantity = (cartItemId: string, quantity: number) => {
  const product = find(
    itemsData.value,
    (item) => item.cartItemId === props.data.cartItemId
  )
  if (product)
    on('remove_from_cart', {
      item_name: product.name,
      item_id: product.artworkSku
        ? `${product.productSku}${product.artworkSku}`
        : product.productSku,
      quantity: quantity
    })
  handleCartStoreDecreaseQuantity(cartItemId)
}
const itemSlug = computed(() =>
  find(items.value, (item) => item.cartItemId === props.data.cartItemId)
)

const handleExtractBase64 = (base64: string) => {
  const index = findIndex(
    items.value,
    (item) => item.cartItemId === props.data?.cartItemId
  )
  if (index === -1) return
  handleUpdateBase64(index, base64)
}

const isStockLoading = ref(true)
const productStocks = ref<
  GetProductAvailableStocksResponse['data'][number] | null
>()
const { $api } = useNuxtApp()
const productRepo = productRepository($api)

const handleCheckProductStock = async () => {
  await productRepo
    .getProductStock({
      body: {
        itec_skus: productData.value?.productSku
          ? [productData.value?.productSku]
          : []
      },
      onResponse: ({ response }) => {
        productStocks.value = response._data.data?.[0]
        if (response._data.data?.[0].stocks < 1) {
          isOutOfStock.value = true
          emit('delete')
        } else if (response._data.data?.[0].stocks < props.data.quantity) {
          handleSetQuantity(
            props.data.cartItemId,
            response._data.data?.[0].stocks
          )
          isOutOfStock.value = false
        } else {
          isOutOfStock.value = false
        }
      }
    })
    .catch((error) => console.log(error))
  isStockLoading.value = false
}

onMounted(async () => {
  await useQuery<GetProductDetailResponse>(
    () => `/v1/storefront/product/detail/${props.data.productSlug || ''}`,
    {
      params: {
        artwork: props.data.artworkSlug,
        device: props.data.deviceSlug
      },
      method: 'GET',
      onResponse: ({ response }) => {
        isLoading.value = false
        const selectedProduct = find(response._data.data, (product) =>
          find(product.product_variant_detail, {
            id: props.data.productVariantDetailId
          })
        ) as unknown as GetProductDetailResponse['data'][number]

        const selectedProductVariant = find(
          selectedProduct?.product_variant_detail,
          {
            id: props.data.productVariantDetailId
          }
        ) as unknown as GetProductDetailResponse['data'][number]['product_variant_detail'][number]

        const payload = {
          cartItemId: props.data.cartItemId,
          name: props.data.personalization
            ? 'Personalized Case'
            : selectedProduct?.artwork_name || selectedProduct?.product_name,
          deviceName: selectedProduct?.device_name,
          productName: selectedProduct?.product_name,
          productSku: selectedProductVariant?.product_sku,
          artworkSku: selectedProductVariant?.style[0]?.style_sku,
          price: Number(selectedProductVariant?.price || 0),
          variantName: selectedProductVariant?.title_en,
          thumbnailUrl: selectedProductVariant?.style[0]
            ? selectedProductVariant?.style?.[0]?.thumbnail_url
            : selectedProductVariant?.thumbnail_url
        }
        updateProductData(payload)
        if (!selectedProductVariant) {
          removeFromCart(props.data.cartItemId)
        }
        handleCheckProductStock()
      }
    }
  )
})

watch([isLoading, isStockLoading, isOutOfStock], (nextValue) => {
  const loaded = !nextValue.some((v) => v === true)
  if (loaded && isOutOfStock.value === false) {
    emit('loaded')
  }
})
</script>
